// Catalog.ID Client — Minting a voucher batch
// ============================================================================
// The buyer's half of the registration sale (registration-wallets §4.1, §4.3).
//
// ── the order of operations IS the mechanism ────────────────────────────────
// A voucher's bits sit in a Worker LOCK keyed on a public key derived from the
// voucher code, and the locks are created by the Stripe WEBHOOK — which has
// nothing to create them against unless the checkout already carried the keys.
// So the codes are minted HERE, on the buyer's device, BEFORE the card is
// charged, and the checkout carries their hashes and their lock keys.
//
// Two consequences follow, and both are deliberate (D16, D19, D26):
//
//   * no server can produce, re-derive or recognise a code — not the CID node,
//     which stores only a hash of the lookup id, and not the Worker, which sees
//     only public keys;
//   * the buyer's own screen is the ONLY copy. A lost code is lost bits until
//     the lock expires, which is why every surface that shows codes also offers
//     Print in the same breath.
//
// DOM-free and dependency-injected: three views and two wizards run this, and
// none of them may drag a print pipeline into its chunk.

import {
    mintVoucherCode, deriveVoucherSecrets, voucherCodeHash,
} from './voucher-code';
import { lockKeyFor } from '../crypto/bitcash';
import { MAX_VOUCHER_QUANTITY, type RegistrationType, type VoucherLockSpec } from '../api/payment';
import type { BitsBounds } from './payment-flow';

export type { BitsBounds };

/** Group a whole number the way the kit and the cards do: thousands separated
 *  by a NON-BREAKING space, so a figure never wraps across a line break. */
function groupDigits(value: number): string {
    return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
}

/**
 * What is wrong with a bits figure, or null when it is inside the bounds. Pure.
 *
 * Bits are MANDATORY on every voucher (D1): a registration without them is the
 * free-starter idea the whitepaper rules out, so there is no "none" to accept
 * here. The Worker enforces the same range at checkout — this only saves the
 * buyer a round trip and a Stripe window.
 */
export function validateBits(value: number, bounds: BitsBounds): string | null {
    if (!Number.isInteger(value)) {
        return 'Enter a whole number of bits.';
    }
    if (value < bounds.min || value > bounds.max) {
        return `Choose between ${groupDigits(bounds.min)} and ${groupDigits(bounds.max)} bits per code.`;
    }
    return null;
}

/**
 * The bits figure with its euro equivalent, when the Worker has told us the
 * rate: `100 000 bits ≈ €20`.
 *
 * This is the ONLY money the client renders (§7). It is not a total and not a
 * price: the fee, the tax and the sum are Stripe's page to draw, because only
 * Stripe knows the buyer's location and whether VAT applies (D21). Rendering a
 * total here would be a second source of truth for a figure that is computed
 * after this screen.
 */
export function bitsEuroLabel(bits: number, bitsPerEuro: number | null): string {
    const figure = `${groupDigits(bits)}\u00a0bits`;
    if (!bitsPerEuro || bitsPerEuro <= 0) return figure;
    const euros = bits / bitsPerEuro;
    // Whole euros read as whole euros; a fraction keeps its cents. Rounded to
    // the cent because that is the smallest unit Stripe will charge.
    const amount = Number.isInteger(euros) ? groupDigits(euros) : groupDigits(Math.floor(euros))
        + `.${Math.round((euros - Math.floor(euros)) * 100).toString().padStart(2, '0')}`;
    return `${figure}\u00a0≈\u00a0€${amount}`;
}

// ── minting a batch BEFORE the checkout opens (§4.3) ───────────────────────
//
// The order of operations is the whole mechanism. A lock is keyed on a public
// key derived from the code, so the key has to exist BEFORE the card is
// charged: the webhook creates the locks, and it has nothing to create them
// against unless the checkout carried them. That is why the codes are minted
// here, on the buyer's device, and why the buyer's own stash is the only place
// they ever exist (D19, D26).

/** One minted voucher, with everything derived from its code. Lives in the
 *  storefront's stash until the codes have been shown, and nowhere else. */
export interface MintedVoucher {
    /** Display form, `RGV-XXXX-XXXX-XXXX-XXXX`. Shown once; never sent. */
    code: string;
    /** What the node stores the hash of. */
    lookupIdHex: string;
    /** The Ed25519 seed that signs a release of this voucher's locks. */
    seed: Uint8Array;
    publicKeyHex: string;
    /** `L(pk, 0)` — the account lock, compact form. */
    lockKey: string;
    /** `L(pk, 1)` — organization vouchers only. */
    founderLockKey?: string;
}

export interface MintedVoucherBatch {
    registrationType: RegistrationType;
    bits: number;
    founderBits?: number;
    vouchers: MintedVoucher[];
    /** Exactly what `POST /api/checkout/create-payment` carries. */
    specs: VoucherLockSpec[];
}

/** The two non-deterministic calls, injectable so a batch is testable without
 *  32 MiB of scrypt per voucher. */
export interface VoucherMintDeps {
    mintCode: typeof mintVoucherCode;
    derive: typeof deriveVoucherSecrets;
}

export const defaultVoucherMintDeps: VoucherMintDeps = {
    mintCode: mintVoucherCode,
    derive: deriveVoucherSecrets,
};

/**
 * Mint `quantity` codes and derive everything the checkout has to carry.
 *
 * SLOTS (§4.1, ruled 2026-09-14): 0 is the account lock — every type has one;
 * 1 is the founder lock and belongs to organization vouchers ALONE; 2 upward
 * are the parking locks a reclaim probes for. A personal voucher that used slot
 * 1 would collide with an organization's founder lock on the same keypair, so
 * the branch here is not cosmetic.
 *
 * `onProgress` exists because the scrypt is ~0.1 s per code and 25 of them is
 * two and a half seconds of a frozen-looking button.
 */
export async function mintVoucherBatch(
    args: {
        registrationType: RegistrationType;
        quantity: number;
        bits: number;
        founderBits?: number;
        onProgress?: (done: number, total: number) => void;
    },
    deps: VoucherMintDeps = defaultVoucherMintDeps,
): Promise<MintedVoucherBatch> {
    const { quantity } = args;
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > MAX_VOUCHER_QUANTITY) {
        throw new Error(`Choose a whole number of codes between 1 and ${MAX_VOUCHER_QUANTITY}.`);
    }
    const organization = args.registrationType === 'organization';

    const vouchers: MintedVoucher[] = [];
    const specs: VoucherLockSpec[] = [];
    for (let i = 0; i < quantity; i++) {
        const code = deps.mintCode();
        const secrets = await deps.derive(code);
        const lockKey = lockKeyFor(secrets.publicKey, 0);
        const founderLockKey = organization
            ? lockKeyFor(secrets.publicKey, 1) : undefined;
        vouchers.push({
            code,
            lookupIdHex: secrets.lookupIdHex,
            seed: secrets.seed,
            publicKeyHex: secrets.publicKeyHex,
            lockKey,
            ...(founderLockKey ? { founderLockKey } : {}),
        });
        specs.push({
            codeHash: voucherCodeHash(secrets.lookupIdHex),
            lockKey,
            publicKey: secrets.publicKeyHex,
            ...(founderLockKey
                ? { founderLockKey, founderPublicKey: secrets.publicKeyHex }
                : {}),
        });
        args.onProgress?.(i + 1, quantity);
    }

    return {
        registrationType: args.registrationType,
        bits: args.bits,
        ...(organization && args.founderBits !== undefined ? { founderBits: args.founderBits } : {}),
        vouchers,
        specs,
    };
}

/**
 * The wire commitment for ONE minted voucher — the entry `create-payment`
 * carries. Split out of {@link mintVoucherBatch} because the wizards mint a
 * single voucher and then need its spec again on the redirect path, where the
 * batch object has not survived a page load.
 */
export function voucherSpecOf(voucher: MintedVoucher): VoucherLockSpec {
    return {
        codeHash: voucherCodeHash(voucher.lookupIdHex),
        lockKey: voucher.lockKey,
        publicKey: voucher.publicKeyHex,
        ...(voucher.founderLockKey
            ? { founderLockKey: voucher.founderLockKey, founderPublicKey: voucher.publicKeyHex }
            : {}),
    };
}
